<template>
  <div>
    <v-dialog
      v-model="showDialog"
      fullscreen
      persistent
      hide-overlay
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-bind="attrs"
          color="primary"
          v-on="on"
        >
          <v-icon>
            {{ icons.mdiUpload }}
          </v-icon>
          KBL hochladen
        </v-btn>
      </template>
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn
            icon
            dark
            @click="showDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            KBL hochladen | Projekt {{ project.projektId }} -
            {{ project.name }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn
              :loading="isSubmitting"
              :disabled="!isTableDataValid"
              dark
              text
              @click="showSubmitDialog = true"
            >
              <v-icon>
                {{ icons.mdiUpload }}
              </v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <v-row>
            <v-col cols="11">
              <v-file-input
                ref="filePicker"
                v-model="file"
                class="my-4"
                counter
                show-size
                label="KBL auswählen"
                :accept="allowedExtensions.join(',')"
                :truncate-length="truncateLength"
                :disabled="false"
                :loading="
                  isReadingFile || isSubmitting || isParsingFile || isValidating
                "
                @change="onFileChange"
              ></v-file-input>
            </v-col>
            <v-col cols="1">
              <v-text-field
                v-if="selectedHeaderRowIndex > 0"
                v-model="selectedHeaderRowIndex"
                class="my-4"
                label="Header Zeile"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-alert
            v-if="errorMessage"
            elevation="0"
            :type="errorType ? errorType : 'error'"
            class="my-4"
            text
            outlined
          >
            <span v-html="errorMessage"></span>
          </v-alert>

          <v-layout column style="max-height: calc(100vh - 240px)">
            <v-flex
              style="overflow-y: auto; var(--goe-borderColor-dark) transparent;"
              class="custom-scrollbar"
            >
              <template v-if="errors.length > 0">
                <v-alert
                  type="error"
                  class="my-4"
                  text
                  outlined
                >
                  <h3>
                    Die Datei kann nicht importiert werden, folgende Probleme
                    wurden festgestellt:
                  </h3>
                  <br />
                  <ul>
                    <li v-for="(error, index) of errors" :key="`${index}error`">
                      <span v-html="error"></span>
                    </li>
                  </ul>
                </v-alert>
              </template>
              <!-- <pre>
                {{ tableItems }}
              </pre>
              <b>ITEMSTATE</b>
              <pre>
                {{ itemState }}
              </pre> -->
              <v-data-table
                v-if="parsedFile && tableItems && tableIsVisible"
                :headers="tableHeaders"
                :items="tableItems"
                :loading="isSubmitting || isValidating"
                :search="tableSearchTerm"
                :items-per-page.sync="tableItemsPerPage"
                disable-sort
                hide-default-header
                @update:items-per-page="
                  updateLocalStorage('itemsPerPage', $event)
                "
              >
                <template v-slot:top>
                  <v-toolbar flat>
                    <div
                      class="pa-2 d-flex justify-space-between align-center"
                      style="width: 100%"
                    >
                      <v-switch
                        v-model="showInvalidOnly"
                        class="mt-6"
                        style="display: flex; justify-content: center"
                        label="Fehlerhafte Einträge anzeigen"
                      ></v-switch>
                      <v-text-field
                        v-model="tableSearchTerm"
                        placeholder="Suche"
                        class="mt-5 px-9"
                      />
                    </div>
                  </v-toolbar>
                </template>
                <template #header="{ props: { headers } }">
                  <thead class="v-data-table-header">
                    <tr>
                      <th></th>
                      <th
                        v-for="header in headers"
                        :key="header.value"
                        class="text-start"
                        role="columnheader"
                        scope="col"
                        :aria-label="header.text"
                      >
                        <v-icon
                          small
                          left
                          class="mr-1"
                        >
                          {{ getIconByType(header) }}
                        </v-icon>
                        {{ header.text }}
                        <template v-if="!selectedHeaders[header.value]">
                          <v-icon
                            v-if="!selectedHeaders[header.value]"
                            color="orange"
                            :title="`Es konnte keine Spalte für ${header.text} gefunden werden`"
                            small
                          >
                            {{ icons.mdiAlertCircle }}
                          </v-icon>
                        </template>
                        <v-icon
                          v-if="freeHeaders.length > 0"
                          class="justify-end"
                          icon
                          small
                          right
                          @click="selectHeaderForCol(header.value)"
                        >
                          {{ icons.mdiTableColumn }}
                        </v-icon>
                      </th>
                    </tr>
                  </thead>
                </template>
                <template #body="{ items, headers }">
                  <tbody>
                    <tr v-for="(item, index) in items" :key="index + '_row'">
                      <td>
                        <v-icon
                          v-if="
                            itemState[item[INTERNAL_KEY_NAME]].state ===
                              ITEM_STATES.DEFAULT
                          "
                          small
                          left
                        >
                          {{ icons.mdiMinus }}
                        </v-icon>
                      </td>
                      <td
                        v-for="header in headers"
                        :key="index + header.value"
                        class="text-start"
                      >
                        <template v-if="selectedHeaders[header.value]">
                          <template
                            v-if="
                              (validationResult = isValueValid(
                                item[header.value],
                                header.value
                              ))
                            "
                          >
                            <v-icon
                              v-if="!validationResult[0]"
                              :color="
                                validationResult[1] ? 'primary' : 'orange'
                              "
                              :title="
                                validationResult[1]
                                  ? 'Optionaler Wert'
                                  : 'Ungültiger Wert'
                              "
                            >
                              {{ icons.mdiAlertCircle }}
                            </v-icon>
                          </template>
                          {{ item[header.value] }}
                        </template>
                        <template
                          v-else-if="header.value === 'houseNumberAddition'"
                        >
                          {{ item.houseNumberAddition }}
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-data-table>
              <div v-if="matchingIsVisible">
                <template v-for="(match, index) in itemState">
                  <div :key="index + '_row'" class="matching-result">
                    <v-row>
                      <StatCard
                        label="Vertrags-Nr."
                        :value="match.matchResult.input.contractId"
                      />
                      <StatCard
                        label="Home-ID"
                        :value="match.matchResult.input.homeId"
                      />
                      <StatCard
                        label="AGS-Key"
                        :value="match.matchResult.input.agsKey"
                      />
                      <StatCard
                        label="PLZ"
                        :value="match.matchResult.input.zip"
                      />
                      <StatCard
                        label="Ort"
                        :value="match.matchResult.input.city"
                      />
                      <StatCard
                        label="Straße"
                        :value="match.matchResult.input.street"
                      />
                      <StatCard
                        label="Hausnummer"
                        :value="match.matchResult.input.housenumber"
                      />
                      <StatCard
                        label="Hausnummernzusatz"
                        :value="match.matchResult.input.housenumbersupplement"
                      />
                      <StatCard
                        label="ONT-Serienr."
                        :value="match.matchResult.input.ontSn"
                      />
                    </v-row>
                    <hr />
                    <v-row>
                      <v-col>
                        <b>Ergebnis PAST:</b>
                        <pre>
                          {{
                            match.matchResult.resultSupplyUnitConnectionsList
                          }}
                        </pre>
                      </v-col>
                      <v-col>
                        <b>Ergebnis Merged-KBL:</b>
                        <pre>
                          {{ match.matchResult.resultMergedKblTable }}
                        </pre>
                        <hr />
                        <b>Ergebnis varioCRM:</b>
                        <pre>
                          {{ match.matchResult.FirebirdResult }}
                        </pre>
                      </v-col>
                    </v-row>
                  </div>
                </template>
              </div>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="!tableIsVisible" @click="tableIsVisible = true">
            Tabelle wieder einblenden
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            :loading="isValidating"
            color="primary"
            @click="validateData(null)"
          >
            Validierung starten
          </v-btn>

          <v-btn
            :loading="isSubmitting"
            :disabled="!isTableDataValid || showInvalidOnly"
            color="primary"
            @click="showSubmitDialog = true"
          >
            Importprozess starten
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showSubmitDialog" width="500">
      <v-card :loading="isSubmitting">
        <v-card-title> Importprozess starten </v-card-title>

        <v-card-text>
          Soll der Import Prozess für <b>{{ tableItemsCount }}</b> Einträge in
          {{ project.projektId }} - {{ project.name }} gestartet werden?
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn color="secondary" @click="showSubmitDialog = false">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            :loading="isSubmitting"
            :disabled="!isTableDataValid"
            color="primary"
            @click="submitData"
          >
            Ja, importieren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showValidateDialog"
      :persistent="isValidating"
      width="500"
    >
      <v-card :loading="isValidating">
        <v-card-title> Validierung </v-card-title>
        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
            color="secondary"
            :disabled="isValidating"
            @click="showValidateDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            :loading="isValidating"
            color="primary"
            @click="validateData(null)"
          >
            Validierung starten
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="selectSheetOptions && selectSheetPromise"
      v-model="selectSheetPromise"
      width="500"
    >
      <v-card>
        <v-card-title>
          Das Dokument enthält mehrere Tabellenblätter
        </v-card-title>

        <v-card-text>
          <v-combobox
            v-model="selectedSheetValue"
            :items="selectSheetOptions"
            placeholder="Tabellenblatt auswählen"
          ></v-combobox>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn color="secondary" @click="userSelectWorksheet">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="selectSheetOptions && selectSheetPromise"
            color="primary"
            :disabled="!selectedSheetValue"
            @click="userSelectWorksheet"
          >
            Auswählen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="selectHeaderPromise"
      v-model="selectHeaderPromise"
      width="500"
    >
      <v-card>
        <v-card-title>
          Spalte für {{ columnNameForSelection }} auswählen
        </v-card-title>

        <v-card-text>
          <v-combobox
            v-model="selectedHeader"
            item-text="value"
            :items="freeHeaders"
            :placeholder="`Spalte für ${columnNameForSelection} auswählen`"
          ></v-combobox>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn color="secondary" @click="userSelectHeaderForCol">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="selectHeaderPromise"
            color="primary"
            :disabled="!selectedHeader"
            @click="userSelectHeaderForCol"
          >
            Auswählen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { HTTP } from '@/main/httpClient.js';
import {
  mdiTableColumn,
  mdiAlertCircle,
  mdiUpload,
  mdiFormatText,
  mdiNumeric,
  mdiMinus
} from '@mdi/js';
import NotificationObject from '@/main/NotificationObject.js';
import StatCard from './Statcard.vue';

const EXCEL_EXTENSION = '.xlsx';
const CSV_EXTENSION = '.csv';

const DEFAULT_SHEET_NAME = 'FieldServicePatchListe-Projekt';
const SETTING_KEY = 'kblImport';

const INTERNAL_KEY_NAME = '__INTERNAL_ID';
const ITEM_STATES = {
  DEFAULT: 'none',
  LOADED: 'loaded'
};

export default {
  name: 'KblUploader',
  components: {
    StatCard
  },
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    INTERNAL_KEY_NAME,
    ITEM_STATES,
    icons: {
      mdiTableColumn,
      mdiAlertCircle,
      mdiUpload,
      mdiFormatText,
      mdiNumeric,
      mdiMinus
    },
    showDialog: false,
    showSubmitDialog: false,
    showValidateDialog: false,
    isSubmitting: false,
    isValidating: false,
    isReadingFile: false,
    isParsingFile: false,
    file: null,
    truncateLength: 0,
    errorType: null,
    errorMessage: null,
    jsonFile: null,
    parsedFile: null,
    selectedHeaderRowIndex: null,
    tableIsVisible: true,
    matchingIsVisible: false,
    // table variables
    tableHeaders: [
      { text: 'AGS', value: 'ags' },
      { text: 'Home ID', value: 'homeId' },
      { text: 'Straße', value: 'street' },
      { text: 'Hausnummer', value: 'houseNumber' },
      { text: 'Zusatz', value: 'houseNumberAddition' },
      { text: 'PLZ', value: 'zipCode' },
      { text: 'Ort', value: 'city' },
      { text: 'Ortsteil', value: 'district' },
      { text: 'Vertragsnummer', value: 'contractId' },
      { text: 'ONT Seriennummer', value: 'ontSerialNumber' },
      { text: 'MFG', value: 'mfg' },
      { text: 'MFG Splice Panel', value: 'mfgSplicePanel' },
      { text: 'MFG Panel Port', value: 'mfgPanelPort' },
      { text: 'MSAN Hostname', value: 'deviceHostname' },
      { text: 'MSAN Slot', value: 'deviceSlot' },
      { text: 'MSAN Port', value: 'devicePort' }
    ],
    headerDefaults: {
      ags: {
        type: String,
        value: ['g-ags-key', 'ags', 'ags27'],
        optionalIfPresent: ['street', 'houseNumber', 'zipCode', 'city']
      },
      homeId: { type: String, value: ['home-id'] },
      street: {
        type: String,
        value: ['straße'],
        optionalIfPresent: ['homeId']
      },
      houseNumber: {
        type: String,
        value: ['hausnummer', 'haus nr.', 'nr.'],
        optionalIfPresent: ['homeId']
      },
      houseNumberAddition: {
        type: String,
        value: ['hausnummernzusatz', 'zusatz'],
        optionalIfPresent: ['homeId']
      },
      zipCode: { type: String, value: ['plz'], optionalIfPresent: ['homeId'] },
      city: {
        type: String,
        value: ['ort', 'gemeinde'],
        optionalIfPresent: ['homeId']
      },
      district: {
        type: String,
        value: ['ortsteil'],
        optionalIfPresent: ['homeId']
      },
      contractId: {
        type: Number,
        value: ['vertragsnummer', 'id_1'],
        optional: true
      },
      ontSerialNumber: {
        type: String,
        value: ['ont_sn', 'fibertwist', 'helper: fiber-twist nr.'],
        optional: true
      },
      mfg: { type: String, value: ['mfg', 'msan'], optional: true },
      mfgSplicePanel: {
        type: String,
        value: ['mfg_spleißpanel', 'mfg_spleisspanel'],
        optional: true
      },
      mfgPanelPort: { type: String, value: ['mfg_panelport'], optional: true },
      deviceHostname: {
        type: String,
        optional: true,
        value: ['device_hostname']
      },
      deviceSlot: {
        type: Number,
        value: ['device_slot', 'slot'],
        optional: true
      },
      devicePort: {
        type: Number,
        value: ['device_port', 'port'],
        optional: true
      }
    },
    tableItemsPerPage: 15,
    showInvalidOnly: false,
    tableSearchTerm: '',
    selectedHeaders: null,
    availableHeaders: null,
    freeHeaders: null,
    selectedHeader: null,
    selectHeaderPromise: null,
    columnNameForSelection: null,
    // excel variables
    selectedSheetValue: null,
    selectSheetPromise: null,
    selectSheetOptions: null,
    debounceTimer: null,
    itemState: {}
  }),
  computed: {
    tableItems() {
      if (this.isAllHeadersMissing) return [];
      const headers = this.selectedHeaders;
      return this.parsedFile
        ?.map((i) => {
          const item = {};
          let isInvalid = !this.showInvalidOnly;
          item[INTERNAL_KEY_NAME] = i[INTERNAL_KEY_NAME];
          for (const key in headers) {
            let value = headers[key] ? i[headers[key]] : null;

            const type = this.headerDefaults[key]?.type;
            if (type === String && !!value) {
              value = value.toString().trim();
            } else if (type === Number && !!value) {
              if (typeof value === 'string') {
                value = parseInt(value);
                if (isNaN(value)) {
                  value = null;
                }
              }
            } else {
              value = null;
            }

            isInvalid = isInvalid || !this.isValueValid(value, key)[0];

            item[key] = value;
          }

          if (headers.houseNumberAddition === null) {
            if (typeof item.houseNumber === 'string') {
              const match = item.houseNumber.match(/^(\d+)(.+)?$/);
              if (match) {
                item.houseNumber = match[1];
                item.houseNumberAddition =
                  match[2]?.trim()?.toLowerCase() || null;
              } else {
                item.houseNumber = null;
                item.houseNumberAddition = null;
              }
            }
          }

          return isInvalid ? item : null;
        })
        .filter((i) => i);
    },
    allowedExtensions() {
      return [EXCEL_EXTENSION, CSV_EXTENSION]; // for now only excel files are supported
    },
    requiredHeaders() {
      return this.tableKeys.map((k) => this.headerDefaults[k].value);
    },
    isAllHeadersMissing() {
      return this.requiredHeaders?.length === this.missingHeaders?.length;
    },
    missingHeaders() {
      return (
        this.selectedHeaders &&
        Object.entries(this.selectedHeaders)
          .map(([key, value]) => {
            if (!value) {
              return this.tableHeaders.find((h) => h.value === key).text;
            }
          })
          .filter((e) => e)
      );
    },
    tableKeys() {
      return Object.keys(this.headerDefaults);
    },
    headerDefaultMap() {
      return this.tableKeys.reduce(
        (map, key) => map.set(key, this.headerDefaults[key]),
        new Map()
      );
    },
    errors() {
      if (!this.parsedFile || !this.tableItems) return [];
      const errors = [];

      if (!this.headerDefaults.contractId.optional) {
        errors.push(
          ...this.getErrorsForField(
            'contractId',
            (contractId, count) =>
              `Für den Vertrag <b>${contractId}</b> befinden sich ${count} Einträge in der Liste`,
            (count) => `${count} Einträge haben keine Vertragsnummer`
          )
        );
      }

      if (!this.headerDefaults.homeId.optional) {
        errors.push(
          ...this.getErrorsForField(
            'homeId',
            (homeId, count) =>
              `${count} Einträge haben die Home ID <b>${homeId}</b>`,
            (count) => `${count} Einträge haben keine Home ID`
          )
        );
      }

      if (!this.headerDefaults.ontSerialNumber.optional) {
        errors.push(
          ...this.getErrorsForField(
            'ontSerialNumber',
            (ontSerialNumber, count) =>
              `Die ONT Seriennummer <b>${ontSerialNumber}</b> befinden sich ${count} mal in der Liste`,
            (count) => `${count} Einträge haben keine ONT Seriennummer`
          )
        );
      }

      return errors;
    },
    isTableDataValid() {
      if (
        this.errors.length > 0 ||
        !this.tableItems ||
        this.tableItems.length === 0
      ) {
        return false;
      }
      for (const item of this.tableItems) {
        for (const key of this.tableKeys) {
          const [isValueValid, isOptional] = this.isValueValid(item[key], key);
          if (!isOptional && !isValueValid) {
            return false;
          }
        }
      }
      return true;
    },
    tableItemsCount() {
      return this.tableItems ? this.tableItems.length : 0;
    }
  },
  watch: {
    jsonFile: function (newVal) {
      if (this.isParsingFile || !newVal) return;
      this.itemState = {};

      this.parseJsonFile(true).then(() => {
        this.isParsingFile = false;
      });
    },
    selectedHeaderRowIndex: function () {
      if (this.isParsingFile || !this.jsonFile) return;
      clearTimeout(this.debounceTimer);

      this.debounceTimer = setTimeout(() => {
        if (this.isParsingFile) return;

        this.itemState = {};
        this.parseJsonFile(false).then(() => {
          this.isParsingFile = false;
        });
      }, 500);
    },
    missingHeaders: {
      handler: function (missingHeaders) {
        if (missingHeaders && missingHeaders.length > 0) {
          this.setWarningMessage(
            `Das Dokument hat nicht alle benötigten Spalten; <code>${this.extensionJoin(
              missingHeaders
            )}`,
            false
          );
        } else if (
          this.errorMessage !== null &&
          this.errorMessage.startsWith(
            'Das Dokument hat nicht alle benötigten Spalten'
          )
        ) {
          this.setErrorMessage(null, 'error', false);
        }
      },
      deep: true
    },
    showDialog: function (newVal) {
      if (newVal && !this.parsedFile) {
        setTimeout(() => {
          // open file selection the first time
          this.$refs.filePicker.$refs.input.click();
        });
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
    setTimeout(this.onResize, 1);

    const settings = this.$settings.get(SETTING_KEY, {});
    this.tableItemsPerPage = settings?.itemsPerPage ?? 15;
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    onResize() {
      const el = this.$refs.filePicker?.$el;
      if (el) {
        this.truncateLength = Math.round(
          (el.getBoundingClientRect().width - 180) / 12
        );
      }
    },
    async onFileChange(file) {
      if (!file) return;
      const reader = new FileReader();
      const self = this;
      this.parsedFile = null;
      this.errorMessage = null;
      this.isReadingFile = true;

      reader.onload = async function (e) {
        try {
          const data = e.target.result;
          if (file.name.endsWith(EXCEL_EXTENSION)) {
            return await self.parseExcel(data);
          } else if (file.name.endsWith(CSV_EXTENSION)) {
            return await self.parseCsv(data);
          }
        } catch (ex) {
          console.error('Error parsing file', ex);
          return self.setErrorMessage(
            'Das ausgewählte Dokument konnte nicht gelesen werden'
          );
        } finally {
          self.isReadingFile = false;
        }

        self.errorMessage = `Ungültiges Dateiformat. Unterstützt werden nur die Dateiformate <code>${self.extensionJoin(
          self.allowedExtensions
        )}</code> unterstützt.`;
      };
      reader.readAsBinaryString(file);
    },
    isValueValid(value, key) {
      if (!key) return [false, false];
      const { type, optional } = this.headerDefaultMap.get(key);
      if (!value || !type) return [false, optional];
      if (type === String) {
        return [
          typeof value === 'string' && value.length > 0,
          optional ?? false
        ];
      }

      if (type === Number) {
        return [typeof value === 'number' && !isNaN(value), optional ?? false];
      }

      return [false, optional];
    },
    async parseExcel(blob) {
      this.jsonFile = null;
      const workbook = XLSX.read(blob, { type: 'binary' });

      const worksheet = await this.getWorksheet(workbook);
      if (!worksheet) {
        return this.setErrorMessage(
          'Das Excel Dokument konnte nicht gelesen werden'
        );
      }

      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        blankrows: false
      });
      if (!jsonData || Object.values(jsonData).length <= 0) {
        return this.setErrorMessage('Das Dokument enthält keine Einträge');
      }

      for (let i = 0; i < jsonData.length; i++) {
        jsonData[i][INTERNAL_KEY_NAME] = i;
      }

      this.jsonFile = jsonData;
    },
    async parseCsv(blob) {
      const delimiter = ',';
      const buffer = Buffer.from(blob, 'binary');
      const decoder = new TextDecoder('utf-8');
      const fileContent = decoder.decode(buffer);
      const allRows = fileContent.split(/[\r\n]+/);

      const headers = allRows
        .at(0)
        .split(delimiter)
        .filter((i) => i.trim() !== '');

      this.availableHeaders = headers;
      this.freeHeaders = [...this.availableHeaders];
      this.selectedHeaders = this.tableKeys.reduce((o, c) => {
        const v = this.headerDefaults[c];
        o[c] = headers.includes(v.value) ? v.value : null;
        return o;
      }, {});

      const rows = allRows.slice(1).map((row) => {
        const parts = row.split(delimiter);
        const item = {};
        for (const h of headers) {
          item[h] = parts[headers.indexOf(h)];
        }
        return item;
      });

      this.freeHeaders = this.freeHeaders.filter(
        (h) => !Object.values(this.selectedHeaders).includes(h)
      );

      this.parsedFile = rows;
    },
    async parseJsonFile(resetHeader = false) {
      this.isParsingFile = true;
      if (resetHeader) {
        this.selectedHeaderRowIndex = null;
      }

      let jsonFile = JSON.parse(JSON.stringify(this.jsonFile));

      const headerRowIndex =
        this.selectedHeaderRowIndex || this.getHeaderRowIndex(jsonFile);
      this.selectedHeaderRowIndex = headerRowIndex;

      let headers;
      const headerRow = jsonFile.at(headerRowIndex);

      if (headerRowIndex === 0) {
        // if first row is not empty loop through all rows because empty values are filtered out
        const headerKeys = [];
        for (let i = 0; i < jsonFile.length; i++) {
          Object.keys(jsonFile.at(i)).forEach((key) => {
            if (!headerKeys.includes(key)) {
              headerKeys.push(key);
            }
          });
        }
        headers = headerKeys.map((key) => ({
          key,
          value: key
        }));
      } else {
        headers = Object.keys(headerRow)
          .map((key) => ({
            key,
            value: headerRow[key]
          })); // get the headers
      }
      jsonFile = jsonFile.splice(headerRowIndex + 1); // remove everything above the header row

      this.availableHeaders = headers.filter(h => h.key !== INTERNAL_KEY_NAME); // filter out internal id
      this.freeHeaders = [...this.availableHeaders];
      this.selectedHeaders = this.tableKeys.reduce((o, c) => {
        const v = this.headerDefaults[c];
        const m = headers.find((h) => {
          return v.value.includes(h.value.toLowerCase());
        });
        o[c] = m?.key || null;
        return o;
      }, {});
      this.freeHeaders = this.freeHeaders.filter(
        (h) => !Object.values(this.selectedHeaders).includes(h.value)
      );

      for (const i of jsonFile) {
        this.itemState[i[INTERNAL_KEY_NAME]] = {
          state: ITEM_STATES.DEFAULT
        };
      }

      this.parsedFile = jsonFile;
    },
    async getWorksheet(workbook) {
      if (workbook.SheetNames.length === 0) {
        return null;
      }

      if (workbook.SheetNames.length > 1) {
        return await this.selectWorksheet(workbook);
      }

      const sheetName = workbook.SheetNames[0];
      return workbook.Sheets[sheetName];
    },
    async selectWorksheet(workbook) {
      const defaultSheetName = DEFAULT_SHEET_NAME;
      const defaultNameIndex = workbook.SheetNames.findIndex(
        (n) => n === defaultSheetName
      );

      if (defaultNameIndex !== -1) {
        return workbook.Sheets[defaultSheetName];
      }

      this.selectSheetOptions = workbook.SheetNames;

      const selectedSheetName = await new Promise((resolve) => {
        this.selectSheetPromise = resolve;
      });

      const selectedNameIndex = workbook.SheetNames.findIndex(
        (n) => n === selectedSheetName
      );

      if (selectedNameIndex !== -1) {
        return workbook.Sheets[selectedSheetName];
      }

      return null;
    },
    userSelectWorksheet() {
      this.selectSheetPromise(this.selectedSheetValue);
      this.selectedSheetPromise = null;
      this.selectSheetOptions = null;
    },
    async selectHeaderForCol(col) {
      this.columnNameForSelection = this.tableHeaders.find(
        (h) => h.value === col
      )?.text;
      const selectedHeader = await new Promise((resolve) => {
        this.selectHeaderPromise = resolve;
      });
      if (!selectedHeader) return;
      const old = this.selectedHeaders[col];
      this.selectedHeaders[col] = selectedHeader.key;
      this.freeHeaders.push(old);
      this.freeHeaders = this.freeHeaders.filter(
        (h) => !!h && h !== selectedHeader && h !== INTERNAL_KEY_NAME
      );
    },
    userSelectHeaderForCol() {
      this.selectHeaderPromise(this.selectedHeader);
      this.selectHeaderPromise = null;
      this.selectedHeader = null;
    },
    getHeaderRowIndex(data) {
      if (!Object.keys(data[0])[0]?.startsWith('__EMPTY_')) {
        return 0;
      }
      // assume the first item with the most values is the header row
      return data.reduce(
        (maxIdx, current, currIdx) =>
          Object.values(current).filter((e) => typeof e === 'string').length >
          Object.values(data[maxIdx]).filter((e) => typeof e === 'string')
            .length
            ? currIdx
            : maxIdx,
        0
      );
    },
    extensionJoin(arr) {
      if (arr.length === 0) return '';
      if (arr.length === 1) return arr[0];
      if (arr.length === 2) return arr.join('</code> und <code>');

      return [
        arr.slice(0, -2).join('</code>, <code>'),
        arr.slice(-2).join('</code> und <code>')
      ].join('</code>, <code>');
    },
    submitData() {
      if (!this.isTableDataValid) return;
      this.isSubmitting = true;

      const data = {
        projectId: this.project.projektId,
        items: this.tableItems
      };

      HTTP.put('gncReport/resourceImport', data)
        .then((res) => {
          this.showMessage('Der import wurde erfolgreich gestartet');
          this.$emit('submitSuccessful');
          this.file = null;
          this.parsedFile = null;
          this.showDialog = false;
        })
        .catch(({ response: { status, data } }) => {
          this.showMessage(`Status ${status}: ${data}`, 'error');
        })
        .finally(() => {
          this.$emit('submitSuccessful');
          this.showSubmitDialog = false;
          this.isSubmitting = false;
        });
    },
    async validateData(items) {
      this.isValidating = true;

      if (items === null) {
        items = this.tableItems;
      }

      if (!Array.isArray(items)) {
        return null;
      }

      const tableChunks = this.chunkArray(
        this.convertToMatchRequest(items),
        100
      );

      for (const chunk of tableChunks) {
        await HTTP.post('past/geo/match-kbl-addresses', chunk)
          .then(({ status, data }) => {
            if (status === 200) {
              this.tableIsVisible = false;
              this.matchingIsVisible = true;
              for (const i of data) {
                this.itemState[i.input.rowId].matchResult = i;
                this.itemState[i.input.rowId].state = ITEM_STATES.LOADED;
              }
            } else {
              this.showMessage('Fehler beim validieren der Einträge', 'error');
            }
          })
          .catch(({ response: { status, data } }) => {
            this.showMessage(`Status ${status}: ${data.message}`, 'error');
          });
      }

      this.isValidating = false;
    },
    convertToMatchRequest: function (item) {
      if (Array.isArray(item)) {
        return item.map((i) => this.convertToMatchRequest(i)).filter((i) => i);
      }

      const newItem = {
        rowId: item[INTERNAL_KEY_NAME],
        zip: item.zipCode ?? null,
        city: item.city ?? null,
        street: item.street ?? null,
        housenumber: item.houseNumber ?? null,
        housenumbersupplement: item.houseNumberAddition ?? null,
        contractId: item.contractId ?? null,
        agsKey: item.ags ?? null,
        homeId: item.homeId ?? null,
        ontSn: item.ontSerialNumber ?? null
      };

      // either zip or city is required
      if (!newItem.zip && !newItem.city) return null;
      if (!newItem.street || !newItem.housenumber) return null;

      return newItem;
    },
    getIconByType(header) {
      const headerType = this.headerDefaults[header.value]?.type ?? String;
      switch (headerType) {
        case Number:
          return this.icons.mdiNumeric;
        case String:
        default:
          return this.icons.mdiFormatText;
      }
    },
    setWarningMessage(message, clearFile = false) {
      this.setErrorMessage(message, 'warning', clearFile);
    },
    setErrorMessage(message, errorType = 'error', clearFile = true) {
      this.errorType = errorType;
      this.errorMessage = message;
      if (clearFile) {
        this.file = null;
      }
    },
    showMessage(message, type = 'success') {
      this.$store.commit(
        'addNotification',
        new NotificationObject(type, message)
      );
    },
    updateLocalStorage: function (key, value) {
      this.$settings.set(`${SETTING_KEY}.${key}`, value);
    },
    getErrorsForField(field, multipleFn, nullFn) {
      if (!field || this.selectedHeaders[field] === null) return [];
      const errors = [];

      const fieldValues = this.tableItems.map((e) => e[field]);
      const valueCounts = {};

      for (const fieldValue of fieldValues) {
        valueCounts[fieldValue] = valueCounts[fieldValue]
          ? valueCounts[fieldValue] + 1
          : 1;
      }

      for (const fieldValue of Object.keys(valueCounts)) {
        if (valueCounts[fieldValue] > 1) {
          if (fieldValue === 'null') {
            errors.push(nullFn(valueCounts[fieldValue]));
          } else {
            errors.push(multipleFn(fieldValue, valueCounts[fieldValue]));
          }
        }
      }

      return errors;
    },
    chunkArray: function (arr, size) {
      const chunks = [];
      for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
      }

      return chunks;
    }
  }
};
</script>

<style scoped>
.scrollable-table {
  max-height: 300px;
  display: flex;
  flex-direction: column;
}

.scrollable-body {
  overflow-y: auto;
  display: block;
  max-height: 200px;
}
.scrollable-body tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}
.matching-result {
  border: 2px solid #ddd;
  border-radius: 5px;
  padding: 15px;
  margin-bottom: 10px;
}
</style>
